import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { ArrowLeft, Search, X, FolderPlus, Users, Lock } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { projectService, userService } from '@/services';
import { useAuth } from '@/hooks/useAuth';

export default function NewProject() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const today = dayjs().format('YYYY-MM-DD');
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    defaultValues: { status: 'active', priority: 'medium', startDate: today, endDate: '' },
  });

  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => userService.list({ limit: 200 }) });
  const userList = users.data?.items || [];

  const [search, setSearch] = useState('');
  // Current user is the project owner — auto-included and locked.
  const [memberIds, setMemberIds] = useState(() => (user?._id ? [user._id] : []));

  // If `user` arrives after first render (e.g. after /auth/me resolves), keep it pinned.
  useEffect(() => {
    if (user?._id) {
      setMemberIds((arr) => (arr.includes(user._id) ? arr : [user._id, ...arr]));
    }
  }, [user?._id]);

  const memberMap = useMemo(() => Object.fromEntries(userList.map((u) => [u._id, u])), [userList]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return userList;
    return userList.filter((u) =>
      [u.name, u.email, u.department, u.designation, u.employeeId]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [userList, search]);

  const isSelf = (id) => user?._id && String(id) === String(user._id);
  const toggleMember = (id) => {
    if (isSelf(id)) return; // owner is locked
    setMemberIds((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  };

  const onSubmit = async (data) => {
    try {
      // Ensure owner is always included even if state was mutated externally.
      const ids = user?._id && !memberIds.includes(user._id) ? [user._id, ...memberIds] : memberIds;
      const payload = { ...data, members: ids };
      if (!payload.endDate) delete payload.endDate;
      const res = await projectService.create(payload);
      toast.success('Project created');
      navigate(`/projects/${res.project._id}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-600">
        <ArrowLeft size={14} /> Back to projects
      </Link>

      <PageHeader
        title="Create project"
        subtitle="Fill in the details and pick teammates to add as members"
      />

      <form id="newProjectForm" onSubmit={handleSubmit(onSubmit)} className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="font-semibold mb-4 flex items-center gap-2"><FolderPlus size={18} /> Project details</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="label">Name</label>
                <input
                  className="input"
                  placeholder="e.g. EDC Web Platform"
                  {...register('name', { required: 'Name is required' })}
                />
                {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name.message}</p>}
              </div>
              <div>
                <label className="label">Code</label>
                <input className="input" placeholder="e.g. EDC-WEB" {...register('code')} />
              </div>
              <div>
                <label className="label">Client</label>
                <input className="input" {...register('client')} />
              </div>
              <div>
                <label className="label">Status</label>
                <select className="input" {...register('status')}>
                  <option value="planning">Planning</option>
                  <option value="active">Active</option>
                  <option value="on-hold">On hold</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="label">Priority</label>
                <select className="input" {...register('priority')}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="label">Start date</label>
                <input type="date" className="input" {...register('startDate')} />
              </div>
              <div>
                <label className="label">End date <span className="text-slate-400 font-normal">(optional)</span></label>
                <input type="date" className="input" {...register('endDate')} />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Description</label>
                <textarea rows={4} className="input" placeholder="What is this project about?" {...register('description')} />
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2"><Users size={18} /> Members</div>
              <span className="text-xs text-slate-500">{memberIds.length} selected</span>
            </div>

            {memberIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {memberIds.map((id) => {
                  const u = memberMap[id] || (isSelf(id) ? user : null);
                  if (!u) return null;
                  const self = isSelf(id);
                  return (
                    <span
                      key={id}
                      className={`inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full text-xs ${
                        self
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                          : 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'
                      }`}
                    >
                      <span className={`w-5 h-5 rounded-full text-white grid place-items-center text-[10px] font-semibold ${
                        self ? 'bg-gradient-to-br from-emerald-500 to-teal-500' : 'bg-gradient-to-br from-brand-500 to-indigo-500'
                      }`}>
                        {u.name?.[0]?.toUpperCase()}
                      </span>
                      {u.name}
                      {self ? (
                        <span className="inline-flex items-center gap-0.5 opacity-70" title="You are the owner — cannot be removed">
                          <Lock size={10} />
                        </span>
                      ) : (
                        <button type="button" onClick={() => toggleMember(id)} className="hover:text-rose-500">
                          <X size={12} />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            <div className="relative">
              <Search size={14} className="absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, department, ID..."
                className="input pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="mt-2 max-h-72 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-100 dark:divide-slate-800">
              {users.isLoading && (
                <div className="p-4 text-sm text-slate-500 text-center">Loading teammates...</div>
              )}
              {!users.isLoading && filtered.length === 0 && (
                <div className="p-4 text-sm text-slate-500 text-center">No teammates match your search.</div>
              )}
              {filtered.map((u) => {
                const checked = memberIds.includes(u._id);
                const self = isSelf(u._id);
                return (
                  <label
                    key={u._id}
                    className={`flex items-center gap-3 p-2.5 text-sm ${
                      self
                        ? 'bg-emerald-50/40 dark:bg-emerald-500/5 cursor-not-allowed'
                        : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                    title={self ? 'You are the owner — automatically included' : undefined}
                  >
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={checked}
                      disabled={self}
                      onChange={() => toggleMember(u._id)}
                    />
                    <div className={`w-8 h-8 rounded-full text-white grid place-items-center font-semibold ${
                      self
                        ? 'bg-gradient-to-br from-emerald-500 to-teal-500'
                        : 'bg-gradient-to-br from-brand-500 to-indigo-500'
                    }`}>
                      {u.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">
                        {u.name}
                        {self && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-emerald-600 dark:text-emerald-400 font-normal">
                            (You · owner) <Lock size={10} />
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {u.email}{u.department ? ` · ${u.department}` : ''}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <div className="font-semibold mb-2">Summary</div>
            <p className="text-sm text-slate-500">
              You'll automatically be added as a member and set as the manager of this project. You can change members
              and details later from the project page.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create project'}
              </button>
              <button type="button" onClick={() => navigate('/projects')} className="btn-secondary w-full">
                Cancel
              </button>
            </div>
          </Card>

          <Card>
            <div className="font-semibold mb-2">Quick tips</div>
            <ul className="text-sm text-slate-600 dark:text-slate-300 space-y-1.5">
              <li>• Pick a short, memorable name.</li>
              <li>• Code (e.g. <b>EDC-WEB</b>) helps spot the project at a glance.</li>
              <li>• Members get a notification when the project is created.</li>
              <li>• You can add or remove members anytime later.</li>
            </ul>
          </Card>
        </div>
      </form>
    </div>
  );
}
